import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FilterIcon } from 'lucide-react';
import { Panel } from '../../components/ui/Panel';
import { PanelHeader } from '../../components/ui/PanelHeader';
import { EmptyState } from '../../components/EmptyState';
import { fleetChecks, workerRegions } from '../../data/adminPlatform';
import { relativeTime } from '../../utils/format';
import type { FleetCheck } from '../../types/admin';
type StateFilter = FleetCheck['state'] | 'all';
const rail: Record<FleetCheck['state'], string> = {
  ok: 'bg-healthy',
  warning: 'bg-warn',
  failing: 'bg-critical',
  paused: 'bg-paused'
};
const detailTone: Record<FleetCheck['state'], string> = {
  ok: 'text-ink-muted',
  warning: 'text-warn-ink',
  failing: 'text-critical-ink',
  paused: 'text-paused-ink'
};
const filters: {
  id: StateFilter;
  label: string;
}[] = [{
  id: 'all',
  label: 'Everything'
}, {
  id: 'failing',
  label: 'Failing'
}, {
  id: 'warning',
  label: 'Warning'
}, {
  id: 'ok',
  label: 'Healthy'
}, {
  id: 'paused',
  label: 'Paused'
}];
export function AdminFleet() {
  const [state, setState] = useState<StateFilter>('failing');
  const [region, setRegion] = useState<string>('all');
  const rows = useMemo(() => fleetChecks.filter(check => {
    if (state !== 'all' && check.state !== state) {
      return false;
    }
    if (region !== 'all' && check.region !== region) {
      return false;
    }
    return true;
  }), [state, region]);
  const counts = useMemo(() => ({
    failing: fleetChecks.filter(c => c.state === 'failing').length,
    warning: fleetChecks.filter(c => c.state === 'warning').length
  }), []);
  return <div className="space-y-5" data-id="element-613" data-mp="8jnk39jd70ku">
      <header data-id="element-614" data-mp="8jdqzd30wdax">
        <h1 className="text-xl font-semibold tracking-tight text-ink" data-id="element-615" data-mp="3h0s37kvendrf">
          Check fleet
        </h1>
        <p className="mt-1 text-sm text-ink-muted" data-id="element-616" data-mp="3c2gtrb90v59q">
          Every check across every account. {counts.failing} failing,{' '}
          {counts.warning} in warning.
        </p>
      </header>

      <Panel data-id="element-617" data-mp="9m1xq4e4brq2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-line px-5 py-3.5" data-id="element-618" data-mp="9lid2fx32p8c">
          <div className="flex flex-wrap gap-1" role="group" aria-label="Filter by check state" data-id="element-619" data-mp="ecqo1ge2ainm">
            {filters.map(option => <button key={option.id} type="button" aria-pressed={state === option.id} onClick={() => setState(option.id)} className={`rounded-control px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-snap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${state === option.id ? 'bg-shell text-white' : 'text-ink-muted hover:bg-canvas hover:text-ink'}`} data-id="element-620" data-mp="3irqf2xfay79o">
                {option.label}
              </button>)}
          </div>

          <div className="ml-auto flex items-center gap-2" data-id="element-621" data-mp="2rk0113d1kzag">
            <FilterIcon className="h-3.5 w-3.5 text-ink-soft" aria-hidden="true" data-id="element-622" data-mp="2rki5gn9uyjtg" />
            <div className="flex flex-wrap gap-1" role="group" aria-label="Filter by region" data-id="element-623" data-mp="2mqneqzvu54kv">
              <button type="button" aria-pressed={region === 'all'} onClick={() => setRegion('all')} className={`rounded border px-2 py-1 font-mono text-[11px] transition-colors duration-150 ease-snap ${region === 'all' ? 'border-ink bg-ink text-white' : 'border-line-strong text-ink-muted hover:text-ink'}`} data-id="element-624" data-mp="2dtd5fk2kvpil">
                all regions
              </button>
              {workerRegions.map(worker => <button key={worker.id} type="button" aria-pressed={region === worker.code} onClick={() => setRegion(worker.code)} className={`rounded border px-2 py-1 font-mono text-[11px] transition-colors duration-150 ease-snap ${region === worker.code ? 'border-ink bg-ink text-white' : 'border-line-strong text-ink-muted hover:text-ink'}`} data-id="element-625" data-mp="54ndmkeov5wf">
                  {worker.code}
                </button>)}
            </div>
          </div>
        </div>

        {rows.length === 0 ? <EmptyState icon={FilterIcon} title="Nothing in this slice" body="No checks match that state and region combination right now — which is usually good news." data-id="element-626" data-mp="3dk72z8c8o467" /> : <ul className="divide-y divide-line" data-id="element-627" data-mp="33m9fwo648df6">
            {rows.map(check => <li key={check.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5 transition-colors duration-150 ease-snap hover:bg-canvas" data-id="element-628" data-mp="2kx7bg04xy6d2">
                <span className={`h-8 w-1 shrink-0 rounded-full ${rail[check.state]}`} aria-hidden="true" data-id="element-629" data-mp="2tyx57cmaphe0" />
                <span className="min-w-0 flex-1" data-id="element-630" data-mp="2p1ngy0an7brr">
                  <span className="block truncate font-mono text-[13px] text-ink" data-id="element-631" data-mp="33vjmxsjbrsbt">
                    {check.domain}
                  </span>
                  <span className={`mt-1 block truncate text-xs ${detailTone[check.state]}`} data-id="element-632" data-mp="2yve1iu3nf226">
                    {check.detail}
                  </span>
                </span>
                <Link to={`/admin/customers/${check.customerId}`} className="text-[13px] text-ink-muted underline decoration-line-strong underline-offset-2 hover:text-ink" data-id="element-633" data-mp="3mn2orw1m50c9">
                  {check.customer}
                </Link>
                <span className="rounded border border-line px-1.5 py-0.5 font-mono text-[11px] text-ink-muted" data-id="element-634" data-mp="2f8h5xe7642v9">
                  {check.kind}
                </span>
                <span className="w-[104px] font-mono text-[11px] text-ink-soft" data-id="element-635" data-mp="2oagmk5e9t4xo">
                  {check.region}
                </span>
                <span className="w-[92px] text-right font-mono text-[11px] text-ink-soft" data-id="element-636" data-mp="2t3taedb4l7pd">
                  {check.durationMs > 0 ? `${check.durationMs}ms` : '—'}
                </span>
                <span className="w-[104px] text-right font-mono text-[11px] text-ink-soft" data-id="element-637" data-mp="332914f68qeyc">
                  {relativeTime(check.lastRunAt)}
                </span>
              </li>)}
          </ul>}
      </Panel>

      <Panel as="section" data-id="element-638" data-mp="1cnyqbq8u1tz3">
        <PanelHeader title="How this list is built" description="Checks are sampled from the last completed pass in each region, not from a live stream." level={2} data-id="element-639" data-mp="1corz0wrcqtjv" />
        <p className="px-5 py-4 text-[13px] leading-relaxed text-ink-muted" data-id="element-640" data-mp="1a42dxakwwiqc">
          A check only appears as failing once it has crossed the customer&apos;s
          own failure threshold — so this list matches what customers were
          actually emailed about, rather than every transient blip the workers
          saw.
        </p>
      </Panel>
    </div>;
}